import { useMemo, useState, type ReactNode } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import { Button, Input, Card, CardContent } from '@/components/ui'

export interface Column<T> {
  key: string
  header: string
  accessor: (item: T) => ReactNode
  sortable?: boolean
  sortValue?: (item: T) => string | number
  className?: string
}

export interface EnterpriseTableProps<T> {
  data: T[]
  columns: Column<T>[]
  isLoading?: boolean
  searchPlaceholder?: string
  searchableKeys?: ((item: T) => string)[]
  filterOptions?: {
    key: string
    label: string
    options: { label: string; value: string }[]
    getValue: (item: T) => string
  }[]
  actions?: (item: T) => ReactNode
  emptyTitle?: string
  emptyDescription?: string
  pageSize?: number
}

export function EnterpriseTable<T>({
  data,
  columns,
  isLoading = false,
  searchPlaceholder = 'Search records...',
  searchableKeys,
  filterOptions,
  actions,
  emptyTitle = 'No records found',
  emptyDescription = 'No entries match your search criteria or filters.',
  pageSize = 10,
}: EnterpriseTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({})
  const [currentPage, setCurrentPage] = useState(1)

  // Filtering
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search
      if (searchTerm.trim() !== '') {
        const query = searchTerm.toLowerCase().trim()
        if (searchableKeys && searchableKeys.length > 0) {
          const matches = searchableKeys.some((fn) => fn(item).toLowerCase().includes(query))
          if (!matches) return false
        } else {
          const strValues = JSON.stringify(item).toLowerCase()
          if (!strValues.includes(query)) return false
        }
      }

      // Filter options
      if (filterOptions && filterOptions.length > 0) {
        for (const filter of filterOptions) {
          const selected = activeFilters[filter.key]
          if (selected && selected !== 'ALL') {
            const itemVal = filter.getValue(item)
            if (itemVal !== selected) return false
          }
        }
      }

      return true
    })
  }, [data, searchTerm, searchableKeys, filterOptions, activeFilters])

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData
    const col = columns.find((c) => c.key === sortKey)
    if (!col || !col.sortable) return filteredData

    return [...filteredData].sort((a, b) => {
      const valA = col.sortValue ? col.sortValue(a) : String(a[sortKey as keyof T] ?? '')
      const valB = col.sortValue ? col.sortValue(b) : String(b[sortKey as keyof T] ?? '')

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortKey, sortDirection, columns])

  // Pagination
  const totalPages = Math.ceil(sortedData.length / pageSize) || 1
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize])

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortKey(null)
        setSortDirection('asc')
      }
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleFilterChange = (filterKey: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [filterKey]: value }))
    setCurrentPage(1)
  }

  return (
    <div className="space-y-4">
      {/* Controls Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
            placeholder={searchPlaceholder}
            className="pl-9 bg-card border-border/80 text-sm focus:ring-primary"
          />
        </div>

        {filterOptions && filterOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <SlidersHorizontal className="size-4 text-muted-foreground shrink-0" />
            {filterOptions.map((filter) => (
              <select
                key={filter.key}
                value={activeFilters[filter.key] ?? 'ALL'}
                onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                className="rounded-lg border border-border/80 bg-card px-3 py-1.5 text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="ALL">All {filter.label}s</option>
                {filter.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ))}
          </div>
        )}
      </div>

      {/* Table Container */}
      <Card className="overflow-hidden border-border/80 bg-card/80 backdrop-blur-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border/60 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSort(col.key)}
                    className={`px-4 py-3.5 select-none ${col.sortable ? 'cursor-pointer hover:text-foreground' : ''} ${col.className ?? ''}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{col.header}</span>
                      {col.sortable && (
                        <span>
                          {sortKey === col.key ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="size-3.5 text-primary" />
                            ) : (
                              <ArrowDown className="size-3.5 text-primary" />
                            )
                          ) : (
                            <ArrowUpDown className="size-3.5 opacity-40 hover:opacity-100" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && <th className="px-4 py-3.5 text-right">Actions</th>}
              </tr>
            </thead>

            <tbody className="divide-y divide-border/40">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-4">
                        <div className="h-4 w-3/4 rounded bg-muted/60" />
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-4 text-right">
                        <div className="ml-auto h-8 w-16 rounded bg-muted/60" />
                      </td>
                    )}
                  </tr>
                ))
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, rowIdx) => (
                  <tr
                    key={rowIdx}
                    className="transition-colors hover:bg-muted/20 text-foreground"
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3.5 font-medium ${col.className ?? ''}`}>
                        {col.accessor(item)}
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        {actions(item)}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (actions ? 1 : 0)} className="py-12 text-center">
                    <div className="mx-auto max-w-sm space-y-2">
                      <Filter className="mx-auto size-8 text-muted-foreground opacity-50" />
                      <p className="font-display text-base font-bold text-foreground">
                        {emptyTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">{emptyDescription}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!isLoading && sortedData.length > 0 && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-border/60 px-4 py-3 text-xs text-muted-foreground">
            <div>
              Showing <span className="font-semibold text-foreground">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-semibold text-foreground">
                {Math.min(currentPage * pageSize, sortedData.length)}
              </span>{' '}
              of <span className="font-semibold text-foreground">{sortedData.length}</span> entries
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="h-8 gap-1 text-xs"
              >
                <ChevronLeft className="size-3.5" /> Previous
              </Button>
              <span className="px-2 font-medium text-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="h-8 gap-1 text-xs"
              >
                Next <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
